import {DocumentMetadata} from "../../entity/model/document-metadata";
import {MarketplaceExcelRequest} from "../../entity/dto/marketplace-excel-request-dto";
import {MarketplaceExcelData, MarketplaceExcelDataUpdate} from "../../entity/dto/marketplace-excel-data-dto";
import {State} from "../../entity/model/state";
import {County} from "../../entity/model/county";
import CountyService from "./county-service";
import {RequestBody} from "../../entity/core/request-body";
import {toast} from "react-toastify";
import MetadataStore from "../store/metadata-store";
import * as ExcelJS from "exceljs";
import {UploadExcelValidationResult} from "../../entity/dto/upload-excel-validation-dto";
import {UploadExcelValidationError} from "../../entity/dto/upload-excel-validation-dto";

export default class MetadataService {
    metadataStore: MetadataStore;

    constructor(metadataStore: MetadataStore) {
        this.metadataStore = metadataStore;
    }

    getPendingMetadataList = async (requestBody: RequestBody): Promise<DocumentMetadata[]> => {
        const response = await this.metadataStore.getPendingMetadataList(requestBody);

        if (typeof response === 'string') {
            toast.error(response);
            return [];
        }

        return response;
    }

    generateMetadataExcel = async (request: MarketplaceExcelRequest): Promise<void> => {
        if (!request.metadata || request.metadata.length === 0) {
            toast.warning('No documents found. Please ensure there are documents available.');
            return;
        }

        const { metadata, states, counties } = request;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Marketplace Data');

        worksheet.columns = [
            {header: 'Document Name', key: 'documentName', width: 30},
            {header: 'State', key: 'state', width: 15},
            {header: 'County', key: 'county', width: 20},
            {header: 'Book Type', key: 'bookType', width: 20},
            {header: 'Index Type', key: 'indexType', width: 15},
            {header: 'Date Range', key: 'dateRange', width: 20},
            {header: 'Index Book', key: 'indexBook', width: 15},
            {header: 'Book', key: 'book', width: 15},
            {header: 'Tags', key: 'tags', width: 20}
        ];

        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' }
        }

        metadata.forEach((item: DocumentMetadata, index: number) => {
            const rowData = {
                documentName: item.documentKey || `Document ${index + 1}`,
                state: '',
                county: '',
                bookType: '',
                indexType: '',
                dateRange: '',
                indexBook: '',
                book: '',
                tags: ''
            };
            worksheet.addRow(rowData);
        });

        if (request.stateObjects && request.countyObjects) {
            request.stateObjects.forEach(state => {
                const stateSheet = workbook.addWorksheet(state.state);

                stateSheet.getCell('A1').value = 'State';
                stateSheet.getCell('B1').value = 'County';

                const headerRow = stateSheet.getRow(1);
                headerRow.font = { bold: true };
                headerRow.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFE0E0E0' }
                };

                const stateCounties = request.countyObjects?.filter(county => county.stateID === state.id) || [];

                stateCounties.forEach((county, index) => {
                    stateSheet.getCell(`A${index + 2}`).value = state.state;
                    stateSheet.getCell(`B${index + 2}`).value = county.name;    
                });
            });
        }

        worksheet.getColumn('bookType').eachCell((cell, rowNumber) => {
            if (rowNumber > 1) 
                cell.dataValidation = {
                    type: 'list',
                    allowBlank: true,
                    formulae: ['"Recorded Documents,Indexes"']
                };
        });

        worksheet.getColumn('indexType').eachCell((cell, rowNumber) => {
            if (rowNumber > 1) 
                cell.dataValidation = {
                    type: 'list',
                    allowBlank: true,
                    formulae: ['"Grantor,Grantee"']
                };
        });

        try {
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = 'marketplace-document-metadata.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            toast.success('Metadata Excel file generated and downloaded successfully!');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Failed to generate Excel file: ${errorMessage}`);
        }
    }

    validateMetadataExcel = async (file: File, metadata?: DocumentMetadata[]): Promise<UploadExcelValidationResult> => {
        try {
            const workbook = new ExcelJS.Workbook();
            const buffer = await file.arrayBuffer();
            await workbook.xlsx.load(buffer);

            const worksheet = workbook.getWorksheet('Marketplace Data');
            if (!worksheet) {
                return {
                    isValid: false,
                    errors: ['Worksheet "Marketplace Data" not found in the uploaded file'],
                    data: []
                };
            }

            const errors: UploadExcelValidationError[] = [];
            const data: MarketplaceExcelData[] = [];

            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return;

                const documentName = row.getCell(1).text || '';

                let id: string;
                if (metadata) {
                    const matchingDoc = metadata.find(doc => doc.documentKey === documentName);
                    id = matchingDoc?.id || `temp-${rowNumber}`;
                } else
                    id = `temp-${rowNumber}`;

                const rowData: MarketplaceExcelData = {
                    id: id,
                    documentName: documentName,
                    state: String(row.getCell(2).text || ''),
                    county: String(row.getCell(3).text || ''),
                    bookType: String(row.getCell(4).text || ''),
                    indexType: String(row.getCell(5).text || ''),
                    dateRange: String(row.getCell(6).text || ''),
                    indexBook: String(row.getCell(7).text || ''),
                    book: String(row.getCell(8).text || ''),
                    tags: String(row.getCell(9).text || '')
                }

                data.push(rowData);

                if (rowData.bookType === 'Indexes') {
                    if (!rowData.indexType?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Index Type',
                            message: 'Index Type is required when Book Type is "Indexes"'
                        });

                    if (!rowData.dateRange?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Date Range',
                            message: 'Date Range is required when Book Type is "Indexes"'
                        });

                    if (!rowData.indexBook?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Index Book',
                            message: 'Index Book is required when Book Type is "Indexes"'
                        });

                    if (rowData.book?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Book',
                            message: 'Book should be empty when Book Type is "Indexes"'
                        });

                } else if (rowData.bookType === 'Recorded Documents') {
                    if (!rowData.book?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Book',
                            message: 'Book is required when Book Type is "Recorded Documents"'
                        });

                    if (rowData.indexType?.trim()) {
                        errors.push({
                            row: rowNumber,
                            column: 'Index Type',
                            message: 'Index Type should be empty when Book Type is "Recorded Documents"'
                        });
                    }
                    if (rowData.dateRange?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Date Range',
                            message: 'Date Range should be empty when Book Type is "Recorded Documents"'
                        });

                    if (rowData.indexBook?.trim())
                        errors.push({
                            row: rowNumber,
                            column: 'Index Book',
                            message: 'Index Book should be empty when Book Type is "Recorded Documents"'
                        });

                }
            });

            const errorMessages = errors.map(error => `Row ${error.row}, ${error.column}: ${error.message}`);

            return {
                isValid: errors.length === 0,
                errors: errorMessages,
                data: data
            };

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            return {
                isValid: false,
                errors: [`Failed to process Excel file: ${errorMessage}`],
                data: []
            };
        }
    }

    validateAndUploadExcel = async (file: File, metadata?: DocumentMetadata[]): Promise<MarketplaceExcelData[]> => {
        const validFileTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];

        const validExtensions = ['.xlsx', '.xls'];

        if (!validFileTypes.includes(file.type) && !validExtensions.some(ext => file.name.endsWith(ext)))
            toast.warning('Only Excel files (.xlsx, .xls) are allowed.');

        let metadataToUse = metadata;
        if (!metadataToUse || metadataToUse.length === 0)
            metadataToUse = await this.getPendingMetadataList({body: {}});

        const response = await this.validateMetadataExcel(file, metadataToUse);
        if (!response.isValid) {
            const errorMessage = response.errors.join('\n');
            toast.error(`Validation failed:\n${errorMessage}`);
        }

        toast.success('Excel file uploaded successfully! Data has been loaded into the table.');
        return response.data;
    }

    submitDocumentMetadata = async (excelData: MarketplaceExcelData[], states: State[], countyService: CountyService): Promise<boolean> => {
        if (excelData.length === 0) {
            toast.warning('Please upload an Excel file first.');
            return false;
        }

        if (states.length === 0) {
            toast.warning('States data not loaded. Please refresh the page.');
            return false;
        }

        try {
            const updateRequests: MarketplaceExcelDataUpdate[] = [];

            for (const row of excelData) {
                const state = states.find(s => s.state === row.state);
                if (!state) {
                    toast.error(`State "${row.state}" not found in the system.`);
                    return false;
                }

                const counties = await countyService.getCounties();
                const county = counties.find((c: County) => c.name === row.county);
                if (!county) {
                    toast.error(`County "${row.county}" not found in state "${state.state}".`);
                    return false;
                }

                const updateRequest: MarketplaceExcelDataUpdate = {
                    id: row.id,
                    documentKey: row.documentName,
                    countyID: county.id,
                    stateID: state.id,
                    bookType: row.bookType,
                    indexType: row.indexType,
                    dateRange: row.dateRange,
                    indexBook: row.indexBook,
                    book: row.book,
                    tags: row.tags
                };

                updateRequests.push(updateRequest);
            }

            const requestBody: RequestBody = {body: updateRequests};
            const response = await this.metadataStore.updateDocumentMetadata(requestBody);

            if (typeof response === 'string') {
                toast.error(response);
                return false;
            } else {
                const failedResults = response.results.filter(result => !result.success);
                const successfulResults = response.results.filter(result => result.success);

                if (failedResults.length === 0) {
                    toast.success(`Excel document(s) updated successfully!`);
                    return true;
                } else if (successfulResults.length === 0) {
                    const errorMessages = failedResults.map(result => `${result.id}: ${result.error}`).join('\n');
                    toast.error(`Excel document(s) failed to update:\n${errorMessages}`);
                    return false;
                } else {
                    const errorMessages = failedResults.map(result => `${result.id}: ${result.error}`).join('\n');
                    toast.warning(`Excel document(s) updated successfully, ${failedResults.length} failed:\n${errorMessages}`);
                    return true;
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Failed to submit Excel document(s): ${errorMessage}`);
            return false;
        }
    }
}
